// API tokens for CI / service-account authentication.
// Tokens are shown ONCE at creation time; only the hash is stored.
//
// Scope vocabulary (item 60):
//   - Bundle: 'pull' | 'publish' | 'admin' (the redesigned UI)
//   - Optional `formats` array narrows artifact ops to those formats
//   - Raw `scopes` are derived server-side and surfaced for display

import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';

export type TokenBundle = 'pull' | 'publish' | 'admin';

export interface Token {
  id: string;
  name: string;
  scopes: string[];
  bundle?: TokenBundle | '';
  formats?: string[];
  createdAt: string;
  lastUsedAt?: string;
  expiresAt?: string;
}

export interface CreatedToken extends Token {
  token: string; // plaintext, only on POST response
}

const fetcher = (url: string) =>
  fetch(url, { credentials: 'include' }).then((r) => {
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.json();
  });

export function useTokens() {
  return useSWR<Token[]>('/api/v1/tokens', fetcher);
}

// Item 97 Phase C: cursor-paginated variant of useTokens. The Tokens
// page switches to this so a CI tenant with hundreds of issued tokens
// gets explicit "Show more" pagination instead of a silent truncation.
interface TokenPage {
  items: Token[];
  next_cursor?: string;
  has_more: boolean;
}

export function useTokensPaginated(limit = 50) {
  const swr = useSWRInfinite<TokenPage>(
    (_idx, prev) => {
      if (prev && !prev.has_more) return null;
      const cursor = prev?.next_cursor ?? '';
      const cursorPart = cursor ? `&cursor=${encodeURIComponent(cursor)}` : '';
      return `/api/v1/tokens?paginated=1&limit=${limit}${cursorPart}`;
    },
    fetcher,
  );
  const items = swr.data ? swr.data.flatMap((p) => p.items) : [];
  const last = swr.data?.[swr.data.length - 1];
  return {
    items,
    error: swr.error,
    isLoading: swr.isLoading,
    isValidating: swr.isValidating,
    hasMore: last?.has_more ?? false,
    loadMore: () => swr.setSize(swr.size + 1),
    mutate: swr.mutate,
    size: swr.size,
  };
}

export interface CreateTokenInput {
  name: string;
  bundle?: TokenBundle;
  formats?: string[];
  // For scripted callers who want to pass raw scopes; the redesigned
  // UI never sends these. If both `bundle` and `scopes` are present
  // the server uses bundle and ignores scopes.
  scopes?: string[];
  expiresIn?: string;
}

export async function createToken(input: CreateTokenInput): Promise<CreatedToken> {
  const res = await fetch('/api/v1/tokens', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function revokeToken(id: string): Promise<void> {
  const res = await fetch(`/api/v1/tokens/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok && res.status !== 204) throw new Error(`revoke failed: ${res.status}`);
}
